import React from "react";
import {
  addPendingNewestCaptureCount,
  createDispatchCaptureCountTracker,
  createDispatchCaptureKeyTracker,
  buildReassignmentCaptureKey,
  buildReportActionCaptureKey,
  DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS,
  DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS,
} from "../lib/dispatchCaptureArrivalHighlight.js";
import {
  subscribeDispatchCaptureArrival,
} from "../lib/dispatchCaptureStatusEvents.js";
import {
  fetchDispatchAlertCaptureConfig,
  fetchReassignmentDiagnosticsHistory,
  fetchReportActionCaptureHistory,
} from "../lib/systemConfigApi.js";
import { fetchJournalCaptureHistory } from "../lib/productUpdatesApi.js";
import { normalizeReassignmentDiagnosticsHistoryPayload } from "../utils/planningLastDiagnostics.js";

function isDocumentVisible() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export default function useDispatchCaptureUnreadIndicators(activeView = "all") {
  const [unreadErrors, setUnreadErrors] = React.useState(false);
  const [unreadReassignment, setUnreadReassignment] = React.useState(false);
  const [unreadSourceChanges, setUnreadSourceChanges] = React.useState(false);
  const [unreadReports, setUnreadReports] = React.useState(false);
  const [arrivingReports, setArrivingReports] = React.useState(false);
  const [reportsArrivalToken, setReportsArrivalToken] = React.useState(0);
  const [unreadJournal, setUnreadJournal] = React.useState(false);
  const [arrivingJournal, setArrivingJournal] = React.useState(false);
  const [journalArrivalToken, setJournalArrivalToken] = React.useState(0);
  const [arrivingSourceChanges, setArrivingSourceChanges] = React.useState(false);
  const [sourceChangesArrivalToken, setSourceChangesArrivalToken] = React.useState(0);
  const [unreadConsolidation, setUnreadConsolidation] = React.useState(false);
  const [arrivingConsolidation, setArrivingConsolidation] = React.useState(false);
  const [consolidationArrivalToken, setConsolidationArrivalToken] = React.useState(0);
  const [arrivingErrors, setArrivingErrors] = React.useState(false);
  const [arrivingReassignment, setArrivingReassignment] = React.useState(false);
  const [errorsArrivalToken, setErrorsArrivalToken] = React.useState(0);
  const [reassignmentArrivalToken, setReassignmentArrivalToken] = React.useState(0);
  const isCombinedActive = activeView === "all";
  const isErrorsActive = activeView === "errors";
  const isSourceChangesActive = activeView === "source_changes" || isCombinedActive;
  const isConsolidationActive = activeView === "consolidation" || isCombinedActive;
  const isReassignmentActive = activeView === "reassignment" || isCombinedActive;
  const isReportsActive = activeView === "reports" || isCombinedActive;
  const isJournalActive = activeView === "journal" || isCombinedActive;

  React.useEffect(() => {
    if (isErrorsActive) {
      setUnreadErrors(false);
    }
    if (isReassignmentActive) {
      setUnreadReassignment(false);
    }
    if (isSourceChangesActive) setUnreadSourceChanges(false);
    if (isConsolidationActive) setUnreadConsolidation(false);
    if (isReportsActive) setUnreadReports(false);
    if (isJournalActive) setUnreadJournal(false);
  }, [isErrorsActive, isReassignmentActive, isSourceChangesActive, isConsolidationActive, isReportsActive, isJournalActive]);

  React.useEffect(() => {
    return subscribeDispatchCaptureArrival((view) => {
      if (view === "reports") {
        if (!isReportsActive) setUnreadReports(true);
        else {
          setArrivingReports(true);
          setReportsArrivalToken((value) => value + 1);
        }
        return;
      }
      if (view === "journal") {
        if (!isJournalActive) setUnreadJournal(true);
        else {
          setArrivingJournal(true);
          setJournalArrivalToken((value) => value + 1);
        }
        return;
      }
      if (view === "errors") {
        if (isErrorsActive) {
          setArrivingErrors(true);
          setErrorsArrivalToken((currentValue) => currentValue + 1);
        } else {
          setUnreadErrors(true);
        }
        return;
      }
      if (view === "source_changes") {
        if (!isSourceChangesActive) setUnreadSourceChanges(true);
        else {
          setArrivingSourceChanges(true);
          setSourceChangesArrivalToken((value) => value + 1);
        }
        return;
      }
      if (view === "consolidation") {
        if (!isConsolidationActive) setUnreadConsolidation(true);
        else {
          setArrivingConsolidation(true);
          setConsolidationArrivalToken((value) => value + 1);
        }
        return;
      }
      if (!isReassignmentActive) {
        setUnreadReassignment(true);
        return;
      }
      setArrivingReassignment(true);
      setReassignmentArrivalToken((currentValue) => currentValue + 1);
    });
  }, [isErrorsActive, isReassignmentActive, isSourceChangesActive, isConsolidationActive, isReportsActive, isJournalActive]);

  React.useEffect(() => {
    if (!arrivingReports || typeof window === "undefined") {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setArrivingReports(false);
    }, DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [arrivingReports, reportsArrivalToken]);

  React.useEffect(() => {
    if (!arrivingJournal || typeof window === "undefined") {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setArrivingJournal(false);
    }, DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [arrivingJournal, journalArrivalToken]);

  React.useEffect(() => {
    if (!arrivingErrors || typeof window === "undefined") {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setArrivingErrors(false);
    }, DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [arrivingErrors, errorsArrivalToken]);

  React.useEffect(() => {
    if (!arrivingReassignment || typeof window === "undefined") {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setArrivingReassignment(false);
    }, DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [arrivingReassignment, reassignmentArrivalToken]);

  React.useEffect(() => {
    if (!arrivingSourceChanges || typeof window === "undefined") {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setArrivingSourceChanges(false);
    }, DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [arrivingSourceChanges, sourceChangesArrivalToken]);

  React.useEffect(() => {
    if (!arrivingConsolidation || typeof window === "undefined") {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setArrivingConsolidation(false), DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [arrivingConsolidation, consolidationArrivalToken]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const trackers = {
      errors: createDispatchCaptureCountTracker(),
      reassignment: createDispatchCaptureKeyTracker(),
      source_changes: createDispatchCaptureKeyTracker(),
      consolidation: createDispatchCaptureKeyTracker(),
      reports: createDispatchCaptureKeyTracker(),
      journal: createDispatchCaptureKeyTracker(),
    };
    const visibleViews = isCombinedActive
      ? new Set(["reassignment", "source_changes", "consolidation", "reports", "journal"])
      : new Set([activeView]);
    const inactiveViews = ["errors", "reassignment", "source_changes", "consolidation", "reports", "journal"].filter((view) => !visibleViews.has(view));
    let cancelled = false;
    let intervalId = null;

    const loadInactiveCounts = async () => {
      if (!isDocumentVisible()) {
        return;
      }
      await Promise.all(inactiveViews.map(async (inactiveView) => {
        try {
          let nextCount = 0;
          let nextKeys = [];
          if (inactiveView === "errors") {
            const payload = await fetchDispatchAlertCaptureConfig();
            nextCount = Number(payload?.capture_count);
          } else if (inactiveView === "journal") {
            const payload = await fetchJournalCaptureHistory({ page: 1, pageSize: 25 });
            nextKeys = (Array.isArray(payload?.items) ? payload.items : []).map(buildReportActionCaptureKey);
          } else if (inactiveView === "reports") {
            const payload = await fetchReportActionCaptureHistory({ page: 1, pageSize: 25 });
            nextKeys = (Array.isArray(payload?.items) ? payload.items : []).map(buildReportActionCaptureKey);
          } else {
            const payload = normalizeReassignmentDiagnosticsHistoryPayload(
              await fetchReassignmentDiagnosticsHistory({ activityScope: inactiveView === "reassignment" ? "dispatch" : inactiveView }),
            );
            nextKeys = payload.items.map(buildReassignmentCaptureKey);
          }
          if (cancelled) {
            return;
          }
          const delta = inactiveView === "errors" ? trackers.errors.ingest(nextCount) : trackers[inactiveView].ingest(nextKeys).length;
          if (delta <= 0) {
            return;
          }
          addPendingNewestCaptureCount(inactiveView, delta);
          if (inactiveView === "errors") {
            setUnreadErrors(true);
          } else if (inactiveView === "source_changes") {
            setUnreadSourceChanges(true);
          } else if (inactiveView === "consolidation") {
            setUnreadConsolidation(true);
          } else if (inactiveView === "journal") {
            setUnreadJournal(true);
          } else if (inactiveView === "reports") {
            setUnreadReports(true);
          } else {
            setUnreadReassignment(true);
          }
        } catch {
          // Le clignotement reste opportuniste: un échec de poll ne doit pas casser l’historique visible.
        }
      }));
    };

    const startPollingInterval = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      if (isDocumentVisible()) {
        intervalId = window.setInterval(loadInactiveCounts, DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS);
      }
    };

    const handleVisibilityChange = () => {
      if (!isDocumentVisible()) {
        if (intervalId != null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      void loadInactiveCounts();
      startPollingInterval();
    };

    void loadInactiveCounts();
    startPollingInterval();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      cancelled = true;
      if (intervalId != null) {
        window.clearInterval(intervalId);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [activeView, isCombinedActive]);

  return {
    arrivingJournal,
    unreadJournal,
    arrivingReports,
    unreadReports,
    arrivingSourceChanges,
    unreadSourceChanges,
    arrivingConsolidation,
    unreadConsolidation,
    arrivingErrors,
    arrivingReassignment,
    unreadErrors,
    unreadReassignment,
  };
}
